// Module 10.6 — the visual encoding, composed behind the FROZEN viz.v1 port. encodeSnapshot is a PURE
// function (Snapshot + EffectsModel + quality → declarative SceneSpec): no Date.now, no random, no
// hashing, no physics — same inputs ⇒ byte-identical spec. createEffectsEncoder is the EffectsEncoder
// the adapter injects: it closes over the app-supplied generators (frozen VizModel omits them), merges
// the per-tick state-selector prosumers/loads into the snapshot, and caches the last spec for the
// adapter/app governor. Boundary: imports only `domain`, the same-module contract, and effects files.
import type { Snapshot, Layer, GridEdge, Load, LoadRuntime, Generator } from "../../domain/index";
import type { QualityTier, EffectsInput, EffectsEncoder } from "../contract";
import type { EffectsModel, SceneSpec } from "./spec";
import { encodeAssets, encodePlants } from "./assets";
import { encodeWalls } from "./parcels";
import { encodeProsumers } from "./prosumer";
import { encodeCrisis } from "./faults";

export const ALL_LAYERS_VISIBLE: Record<Layer, boolean> = { energy: true, information: true, finance: true };

export function encodeSnapshot(
  snapshot: Snapshot,
  model: EffectsModel,
  quality: QualityTier,
  visible: Record<Layer, boolean> = ALL_LAYERS_VISIBLE,
): SceneSpec {
  // Phase 0.4: the dead producers (encodeFlows, encodeFaults, outageNodeIds) and the 8 dead SceneSpec
  // fields they populated (energyFlows, arcs, faults, outageNodes, material, flags, layersVisible,
  // quality) are REMOVED. Only the 5 LIVE fields remain: plants, walls, assets, prosumers, crisis.
  // The `quality` and `visible` params are kept (createEffectsEncoder passes them; future phases may
  // use them for new live fields) but no longer echo into the spec.
  void quality; // accepted for API stability; the central TIER_KNOBS table is read by the adapter directly
  void visible;
  const outageSet = new Set<string>(snapshot.outages.map((o) => o as string));

  const edgeById = new Map<string, GridEdge>();
  for (const e of model.edges) edgeById.set(e.id as string, e);
  const loadById = new Map<string, Load>();
  for (const l of model.loads) loadById.set(l.id as string, l);
  const loadRuntimeById = new Map<string, LoadRuntime>();
  for (const r of snapshot.loads ?? []) loadRuntimeById.set(r.id as string, r);

  return {
    plants: encodePlants(snapshot.dispatch, model.generators),
    walls: encodeWalls(model.loads, loadRuntimeById),
    assets: encodeAssets(model.nodes, outageSet, snapshot.branchFlows, edgeById), // P14: status from incident loading
    prosumers: encodeProsumers(snapshot.prosumers, loadById),
    crisis: encodeCrisis(snapshot.branchFlows),
  };
}

export function createEffectsEncoder(extra?: { generators?: Generator[] | undefined }): EffectsEncoder & { readonly lastSpec: SceneSpec | null } {
  const generators = extra?.generators;
  let lastSpec: SceneSpec | null = null;
  return {
    encode(input: EffectsInput): SceneSpec {
      const model: EffectsModel = {
        nodes: input.model.nodes,
        edges: input.model.edges,
        loads: input.model.loads,
        ...(generators ? { generators } : {}),
      };
      // state-selector prosumers/loads are authoritative — fold them into the snapshot the encoders read.
      const snapshot: Snapshot = { ...input.snapshot, prosumers: input.prosumers, loads: input.loads };
      lastSpec = encodeSnapshot(snapshot, model, input.quality, input.layersVisible);
      return lastSpec;
    },
    get lastSpec() {
      return lastSpec;
    },
  };
}

export type {
  SceneSpec, EffectsModel, PlantSpec, WallSpec, AssetSpec, ProsumerSpec,
  CrisisSpec, QualityFlags,
} from "./spec";
